import { filter, find, first, isEmpty, map, omit, size } from 'underscore';
import React, { useCallback, useContext, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Stack } from '@fluentui/react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { useWizardService } from '@bingads-webui-react/wizard';
import { withUiBlock } from '@bingads-webui/uiblocker';

import { useAdsRepository } from '../add-text/use-ads-repository';
import { StepHeader } from '../../shared/step-header';
import { AdTypeMaxCount } from '../../constants/ad-type';
import { AdList } from './ad-list';
import { CreateAdButton } from './create-ad-button';
import { getStyles } from './styles';

export const AdsManager = ({
  data,
  onDataChange,
  adTypes,
  description,
}) => {
  const containerRef = useRef();
  const classes = useDefaultStyles(getStyles);
  const i18n = useContext(I18nContext);

  const { state } = useWizardService();
  const { websites } = state.context.wizardData;

  const { ads = [] } = data;
  const availableAdTypes = useMemo(() => map(adTypes, (adType) => {
    const filteredAds = filter(ads, ad => ad.Type === adType);
    return {
      adType,
      ads: filteredAds,
      disabled: size(filteredAds) >= AdTypeMaxCount[adType],
    };
  }), [adTypes, ads]);

  const adsRepository = useAdsRepository({ enableAdsManagment: true, adTypes });

  const onCreateAd = useCallback((adType) => {
    const anotherAdType = find(availableAdTypes, type => type.adType !== adType);
    const enableAdTypeSelector = anotherAdType && !anotherAdType.disabled;

    onDataChange({
      data: {
        ...data,
        ads: {
          [adType]: {
            data: {
              FinalUrls: websites,
            },
            isValid: false,
          },
        },
        adType,
        enableAdTypeSelector,
      },
      isValid: false,
    });
  }, [availableAdTypes, data, onDataChange, websites]);

  const onCopyAd = useCallback((ad) => {
    const anotherAdType = find(availableAdTypes, type => type.adType !== ad.Type);
    const enableAdTypeSelector = anotherAdType && !anotherAdType.disabled;

    onDataChange({
      data: {
        ...data,
        ads: {
          [ad.Type]: {
            data: omit(ad, 'Id'),
            isValid: true,
          },
        },
        adType: ad.Type,
        enableAdTypeSelector,
      },
      isValid: true,
    });
  }, [availableAdTypes, data, onDataChange]);

  const onEditAd = useCallback((ad) => {
    const anotherAdType = find(availableAdTypes, type => type.adType !== ad.Type);
    const enableAdTypeSelector = anotherAdType && !anotherAdType.disabled;

    onDataChange({
      data: {
        ...data,
        ads: {
          [ad.Type]: {
            data: ad,
            isValid: true,
          },
        },
        adType: ad.Type,
        enableAdTypeSelector,
      },
      isValid: true,
    });
  }, [availableAdTypes, data, onDataChange]);

  const onDeleteAd = useCallback((ad) => {
    adsRepository
      .deleteAd(ad)
      .then(() => {
        const updatedAds = ads.filter(item => item.Id !== ad.Id);
        if (isEmpty(updatedAds)) {
          onDataChange({
            data: {
              ...data,
              ads: adTypes.reduce((acc, adType) => {
                acc[adType] = {
                  data: {
                    FinalUrls: websites,
                  },
                  isValid: false,
                };
                return acc;
              }, {}),
              adType: first(adTypes),
              isEmpty: true,
            },
            isValid: false,
          });
        } else {
          onDataChange({
            data: {
              ...data,
              ads: updatedAds,
            },
            isValid: true,
          });
        }
      })
      .finally(withUiBlock(containerRef.current));
  }, [adTypes, ads, adsRepository, data, onDataChange, websites]);

  return (
    <div className={classNames(classes.adsManager, 'add-text-step-manager')} ref={containerRef}>
      <StepHeader
        title={_TL_('Create your ads')}
        description={description}
      />
      <CreateAdButton
        i18n={i18n}
        adTypes={availableAdTypes}
        onCreateAd={onCreateAd}
      />
      <Stack
        className="ad-list-container"
        horizontal
        tokens={{ childrenGap: 16 }}
      >
        {map(availableAdTypes, ({ adType, ads: adList, disabled }) => (
          <AdList
            key={adType}
            i18n={i18n}
            adType={adType}
            ads={adList}
            onCopyAd={onCopyAd}
            onEditAd={onEditAd}
            onDeleteAd={onDeleteAd}
            enableCopy={!disabled}
          />
        ))}
      </Stack>
    </div>
  );
};

AdsManager.propTypes = {
  data: PropTypes.shape({
    ads: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  onDataChange: PropTypes.func.isRequired,
  adTypes: PropTypes.arrayOf(PropTypes.string),
  description: PropTypes.string,
};

AdsManager.defaultProps = {
  adTypes: [],
  description: '',
};
